import type { ApprovalChain, ChainQuorum } from '@felix/domain';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  Radio,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconPencil, IconPlus, IconRoute, IconTrash } from '@tabler/icons-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import { errorCode } from '../personnel/source.js';
import {
  type ChainDraft,
  type ChainRole,
  chainDraftProblem,
  chainSummary,
  chainToDraft,
  draftToChain,
  rankLabel,
  rankShort,
  shownChain,
} from './approval-chain.js';
import classes from './Documents.module.css';
import type { OfficeDocumentActions } from './source.js';
import type { LibraryDetail } from './types.js';

/**
 * Form onay zinciri editörü (ADR 0071 §2; sahip kararı 2026-10-17): sade adım listesi.
 * Hazırlayanlar (1–2 rütbe, paralel) → ön onaylar (0–2, sıralı) → Master. ISM formu ofis onayına
 * gitmez, ofis yalnız görür (sahip kararı 2026-10-17, ADR 0073): ofis adımı seçilemez. İki rütbeli
 * adımda "hepsi" ya da "biri yeter" seçilir (tek rütbede ikisi aynıdır). "Default" onay
 * matrisinin kuralıdır (hazır ayar; eski formlar böyle sürer).
 */
export function ApprovalChainEditor({
  value,
  roles,
  error,
  disabled = false,
  onChange,
}: {
  value: ChainDraft;
  roles: readonly ChainRole[];
  /** Doğrulama kodu (`documents.validation.<kod>`). */
  error?: string | null | undefined;
  disabled?: boolean;
  onChange: (next: ChainDraft) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.chain.${key}`, options ?? {});
  const data = roles.map((role) => ({ value: role.id, label: rankLabel(role.name) }));
  const name = (id: string) => roles.find((role) => role.id === id)?.name ?? '—';
  const set = (patch: Partial<ChainDraft>) => onChange({ ...value, ...patch });
  const quorum = (current: ChainQuorum, onQuorum: (q: ChainQuorum) => void, label: string) => (
    <SegmentedControl
      size="xs"
      aria-label={label}
      disabled={disabled}
      value={current}
      onChange={(next) => onQuorum(next as ChainQuorum)}
      data={[
        { value: 'all', label: k('quorumAll') },
        { value: 'any', label: k('quorumAny') },
      ]}
    />
  );
  const chain = draftToChain(value);
  const problem = error ?? null;
  return (
    <Stack gap="sm" className={classes.chainEditor} data-chain-editor>
      <Radio.Group
        label={k('title')}
        description={k('hint')}
        value={value.custom ? 'custom' : 'default'}
        onChange={(next) =>
          set({
            custom: next === 'custom',
            // Master adımı varsayılan olarak şirketin "Master" rolü
            master: value.master ?? roles.find((role) => role.name === 'Master')?.id ?? null,
          })
        }
      >
        <Group gap="lg" mt={6}>
          <Radio value="default" label={k('default')} disabled={disabled} />
          <Radio value="custom" label={k('custom')} disabled={disabled} />
        </Group>
      </Radio.Group>
      {value.custom ? (
        <ol className={classes.chainSteps}>
          <li className={classes.chainStep}>
            <MultiSelect
              label={k('preparers')}
              description={k('preparersHint')}
              data={data}
              maxValues={2}
              searchable
              disabled={disabled}
              value={value.preparers}
              onChange={(preparers) => set({ preparers })}
            />
            {value.preparers.length > 1
              ? quorum(
                  value.prepareQuorum,
                  (prepareQuorum) => set({ prepareQuorum }),
                  k('preparers'),
                )
              : null}
          </li>
          {value.reviews.map((role, index) => (
            // Sıra adımın kimliğidir: ön onaylar yalnız sondan eklenir ve çıkarılır
            // biome-ignore lint/suspicious/noArrayIndexKey: adımın yeri anahtardır
            <li key={index} className={classes.chainStep}>
              <Group gap="xs" align="flex-end" wrap="nowrap">
                <Select
                  className={classes.chainSelect}
                  label={k('review', { step: index + 1 })}
                  data={data}
                  searchable
                  disabled={disabled}
                  value={role}
                  onChange={(next) =>
                    set({ reviews: value.reviews.map((r, i) => (i === index ? next : r)) })
                  }
                />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  disabled={disabled}
                  aria-label={k('removeReview', { step: index + 1 })}
                  onClick={() => set({ reviews: value.reviews.filter((_, i) => i !== index) })}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </li>
          ))}
          {value.reviews.length < 2 ? (
            <li className={classes.chainAdd}>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} />}
                disabled={disabled}
                onClick={() => set({ reviews: [...value.reviews, null] })}
              >
                {k('addReview')}
              </Button>
            </li>
          ) : null}
          <li className={classes.chainStep}>
            <Select
              label={k('master')}
              description={k('masterHint')}
              data={data}
              searchable
              disabled={disabled}
              value={value.master}
              onChange={(master) => set({ master })}
            />
          </li>
        </ol>
      ) : null}
      {chain && !problem ? (
        <Text size="sm" className={classes.chainSummary} data-chain-summary>
          {k('summary', { chain: chainSummary(chain, name) })}
        </Text>
      ) : null}
      {problem ? (
        <Text size="sm" c="red" role="alert">
          {t(`documents.validation.${problem}`, {
            defaultValue: t('documents.validation.INVALID'),
          })}
        </Text>
      ) : null}
    </Stack>
  );
}

/**
 * Doküman ayrıntısında "Approval chain" kartı (yalnız ofiste, form türünde; sağ sütunun başında):
 * zincirin adımları okunur ve düzenleme yetkisi olana "Edit approval chain" penceresi. Zincir
 * yoksa hazır kural açıkça yazılır ("Master approves"). Ortak formun kimin onayladığını
 * bulamıyordu (geri bildirim 2026-10-17; ADR 0073 §3). Kayıt `PATCH /office/documents/:id` ile;
 * değişiklik yalnız sonraki gönderimlere işler (ADR 0071 §3).
 */
export function ApprovalChainPanel({
  detail,
  office,
  canEdit,
  onSaved,
}: {
  detail: LibraryDetail;
  office: OfficeDocumentActions;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.chain.${key}`, options ?? {});
  const [roles, setRoles] = useState<ChainRole[]>([]);
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<ChainDraft | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const summaryId = useId();
  const chain = shownChain(detail.approvalChain);

  useEffect(() => {
    let live = true;
    void office.chainRoles().then(
      (list) => live && setRoles(list),
      () => live && setRoles([]),
    );
    return () => {
      live = false;
    };
  }, [office]);

  const name = (id: string) => roles.find((role) => role.id === id)?.name ?? '—';
  const open = () => {
    setDraft(chainToDraft(chain, roles));
    setProblem(null);
    setFailure(null);
    setOpened(true);
  };
  const save = async () => {
    if (!draft) return;
    const code = chainDraftProblem(draft);
    setProblem(code);
    if (code) return;
    setBusy(true);
    setFailure(null);
    try {
      await office.updateDocument(detail.id, detail.rowVersion, {
        approvalChain: draftToChain(draft),
      });
      setOpened(false);
      onSaved();
    } catch (error) {
      setFailure(errorCode(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title={k('title')}
      icon={<IconRoute size={17} stroke={1.8} />}
      className={classes.sideCard}
    >
      <div id={summaryId}>
        {chain ? (
          <ol className={classes.chainView} data-chain-steps>
            {chain.steps.map((step, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: adımın yeri anahtardır
              <li key={index}>
                <span className={classes.chainViewLabel}>
                  {step.kind === 'prepare'
                    ? k('preparers')
                    : step.kind === 'review'
                      ? k('review', {
                          step:
                            chain.steps.slice(0, index).filter((s) => s.kind === 'review').length +
                            1,
                        })
                      : k('master')}
                </span>
                <span>
                  {step.roleIds
                    .map((id) => rankShort(name(id)))
                    .join(step.quorum === 'all' ? ' + ' : ' / ')}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <Text size="sm" data-chain-summary>
            {k('defaultSummary')}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt={6}>
          {k('officeNote')}
        </Text>
      </div>
      {canEdit ? (
        <Button
          variant="default"
          size="xs"
          mt="sm"
          leftSection={<IconPencil size={14} />}
          onClick={open}
          aria-describedby={summaryId}
        >
          {k('edit')}
        </Button>
      ) : null}
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={k('dialogTitle', { document: `${detail.docNo} ${detail.title}` })}
        size="lg"
      >
        {draft ? (
          <Stack gap="md">
            <Text size="sm">{k('appliesNext')}</Text>
            {failure ? (
              <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
                {t(`documents.errors.${failure}`, { defaultValue: t('documents.errors.GENERIC') })}
              </Alert>
            ) : null}
            <ApprovalChainEditor
              value={draft}
              roles={roles}
              error={problem}
              disabled={busy}
              onChange={(next) => {
                setDraft(next);
                setProblem(null);
              }}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setOpened(false)}>
                {t('documents.drawer.cancel')}
              </Button>
              <Button loading={busy} onClick={() => void save()}>
                {k('save')}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Panel>
  );
}

/** Kayıtlı zincirin ayrıntı tipi (ADR 0071 §2). */
export type { ApprovalChain };
